<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <form class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ edit ? $t('projects.estates.update') : $t('projects.estates.create') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="loadReset">
                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                          fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                          fill="currentColor" />
                  </svg>
                </span>
            </div>
          </div>

          <div class="modal-body">
            <RequiredText />
            <div class="row mb-5">
              <Input
                container-class="col-12 form-group"
                :name="`estate_name_`+id"
                type="text"
                :label="$t('projects.estates.name')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-lg form-control-solid'"
                size="lg"
                :placeholder="$t('projects.estates.name')"
                :isRequired="true" 
                v-model="formData.name"
              />
            </div>
            <div class="row mb-5 d-flex align-items-center" v-for="(estate, key) in formData.caracteristics" :key="key">
              <Select2
                  container-class="form-group col-12 col-md-5"
                  :multiple="false"
                  :show-search="true"
                  :selectClass="'form-select form-control-sm form-select-solid'"
                  :name="`caracteristics_id_`+id+key"
                  :label="$t('projects.estates.caracteristic')"
                  labelClass="form-label fs-6 text-black"
                  :placeholder-text="$t('projects.estates.caracteristic_placeholder')"
                  v-model="estate.id"
                  :parent-id="'#'+id"
                  :IsInputGroup="true"
                  icon="bi bi-plus"
                  :clickAddon="openCreationModal"
              >
                <option v-for="caracteristic in caracteristics" :value="caracteristic.id" :key="caracteristic.id">
                  {{ caracteristic.name }}
                </option>
              </Select2>
              <Input
                container-class="form-group col-12 col-md-6"
                :name="`caracteristics_value_`+id+key"
                type="text"
                :label="$t('projects.estates.caracteristic_value')"
                labelClass="form-label fs-6 text-black"
                :inputClass="'form-control form-control-lg form-control-solid'"
                size="lg"
                :placeholder="$t('projects.estates.caracteristic_value_placeholder')"
                :isRequired="true" 
                v-model="estate.value"
              />
              <div class="col-12 col-md-1 d-flex justify-content-end" style="margin-top: 20px;">
                <div :style="{'border': '1px solid '+ $getCssVariable('--bs-danger') }" class="btn btn-icon btn-icon-muted btn-active-light w-40px h-40px" type="button" @click="removeEstate(key)">
                  <i class="fas fa-trash fs-3 text-danger"></i>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end">
              <div :style="{'border': '1px solid '+ $getCssVariable('--bs-primary') }" class="btn btn-md btn-active-light" type="button" @click="addEstate">
                <i class="fas fa-plus fs-3 text-primary me-3"></i> <span class="text-primary">{{ $t('projects.estates.add_char') }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" @click="loadReset" :id="id+'_close_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.close"></Button>
            <Button type="submit" @click="submitData" :id="id+'_save_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.save" :isLoading="isLoadingEstate"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
  <PropertySpecificationFormModal :id="'create-property-specification-modal'+id" :parent="id" @callback="loadCharacteritics"/>
</template>

<script setup>
  import {ref, defineProps, onMounted, computed, watch} from 'vue'
  import Button from "@/components/Button.vue"
  import Input from "@/components/Input.vue";
  import RequiredText from "@/components/RequiredText.vue";
  import { useStore } from "vuex";
  import Select2 from "@/components/Select2.vue";
  import { useRoute } from "vue-router";
  import i18n from "@/plugins/i18n";
  import PropertySpecificationFormModal from "@/Modules/Projects/components/Modals/PropertySpecificationFormModal.vue";

  const store = useStore()
  const route = useRoute()

  const emits = defineEmits(['load'])

  const props = defineProps({
      id: {
        type: String,
        required: true
      },
      form: {
        type: Object,
        default: {
          id: '',
          name: '',
          project_id: '',
          caracteristics: [
            {
              id: '',
              value: ''
            }
          ]
        }
      },
      edit: {
        type: Boolean,
        default: false
      }
  })

  const formData = ref({...props.form})
  const isLoadingEstate = ref(false)

  const caracteristics = computed(() => store.getters['propertySpecifications/getPropertySpecifications'])

  watch(
    () => props.form,
    (newVal) => {
      formData.value = { ...newVal}
    },
    { deep: true }
  )

  function addEstate(){
    formData.value.caracteristics.push({
      id: '',
      value: ''
    })
  }

  function removeEstate(key){
    if(formData.value.caracteristics?.length > 1){
      formData.value.caracteristics.splice(key, 1);
    }
  }

  function reset(){
    formData.value = {
      id: '',
      name: '',
      project_id: '',
      caracteristics: [
        {
          id: '',
          value: ''
        }
      ]
    }
  }

  function loadReset() {
    closeModal()
    reset()
  }

  function submitData() {
    isLoadingEstate.value = true
    formData.value.project_id = route.params.id
    let action = props.edit ? 'projects/updateProjectEstate' : 'projects/storeProjectEstate'
    let message = props.edit ? 'notification.estate_updated' : 'notification.estate_created'

    store.dispatch(action, formData.value)
      .then((response) => {
          emits('load')
          isLoadingEstate.value = false
          loadReset()
          window.toastr['success'](i18n.global.t(message))
      })
      .catch(err => {
          isLoadingEstate.value = false
      })
  }

  function closeModal(){
    $('#'+props.id).modal('toggle')
  }

  function loadCharacteritics(item) {
    store.dispatch('propertySpecifications/loadPropertySpecifications').then(() => {
      if(item){
        formData.value.caracteristics[formData.value.caracteristics?.length - 1].id = item.id
      }
    })
  }

  function openCreationModal() {
    closeModal()
    $('#create-property-specification-modal'+props.id).modal('toggle')
  }

  onMounted(() => {
    if(route.query.tab == 'kt_estates') loadCharacteritics()
  })
</script>